import {useState} from "react";
import {BOOKING_CONSTRAINTS, POLICY_CONSTANTS} from "@/constants/booking";
import {closeRed, infoIconGrey, supportingPerson} from "@img/index.js";

// Shared tabbed content for the "Cancellation / Modification / Refund" info popup.
// Rendered inside a desktop `.upper-modal` popup AND the mobile
// `cancellationModificationRefund` step — only the shell differs.
//
// Every row is derived from the /calculate-policies response tiers (POLICY_CONSTANTS)
// — one row per tier:
//  - Cancellation → `cancellation.tiers` (flat_fee_cents + is_free + label).
//  - Modification → `modification.tiers` (editable_fields + label).
//  - Refund → the SAME cancellation tiers, showing each tier's flat fee ($X) when
//    `flat_fee_cents` > 0, else its `refund_percentage` (%), + label.
//
// An EMPTY tiers array is meaningful, not "not loaded": the backend imposes no bands on
// that action (no admin-configured matrix for this trip_direction / distance band).
// Cancellation and Refund then render FREE_FALLBACK's "unconditionally free" row.
//
// Modification is FROM-AIRPORT ONLY: its fallback copy is about the flight-driven pickup,
// which exists only when the pickup IS the airport. On to-airport / others the tab drops
// the row entirely and shows just the ask-me pill. The copy itself also splits by
// booking-flow vs trip-page — see FREE_FALLBACK.modification{Booking,Trip}.
//
// The trailing notes: Refund's bank-timing note renders in EVERY flow (its copy doesn't
// describe a band). Modification's "Flight delayed?" note renders ONLY when the trip can
// be modified (tiers present) — the fallback row's own copy covers that case — but the
// ask-me pill closes out BOTH branches. Cancellation's note explains where the fee goes,
// so it renders only alongside actual fee tiers (no tiers ⇒ no fee ⇒ nothing to explain).
const TABS = [
    {key: 'cancellation', label: 'Cancellation'},
    {key: 'modification', label: 'Modification'},
    {key: 'refund', label: 'Refund'},
];

// ── Once the driver is on the way ──
//
// From EnRoute onward every tab collapses to ONE line saying the thing no longer applies.
// The tier ladder is a schedule of what it costs to change your mind BEFORE the driver
// commits; after they have set off it answers a question the passenger can no longer ask,
// and leaving it up implies options that are gone.
//
// One sentence rather than a styled panel, on purpose: the answer is "no", and the
// shortest honest way to say it is the kindest.
const UNDERWAY_NOTICE = {
    cancellation: 'You can’t cancel once the trip is enroute',
    modification: 'You can’t modify once the ride is enroute',
    refund: 'Refund is not applicable once the ride is enroute',
};

/** The red ✕ and one line — the whole panel while a trip is under way. */
function BlockedNotice({children}) {
    return (
        <div className="hopn-cmr-blocked">
            {/* `closeRed` — the house cross, the same asset the Cancel control uses. */}
            <img src={closeRed} alt="" aria-hidden="true"/>
            <p className="mb-0!">{children}</p>
        </div>
    );
}

// The empty-tiers row per tab — same TierRow shape as a real tier. Cancellation/Refund
// read "unconditionally free"; Modification instead says no change is NEEDED (the
// flight-driven auto-adjust covers it), still at $0 so the three tabs look alike.
// Modification's row is from-airport only — see the header note.
const FREE_FALLBACK = {
    cancellation: {value: '$0', title: 'Free cancellation', subtitle: 'You can cancel your booking any time'},
    // Modification has TWO variants, picked by whether a trip was supplied:
    //  - booking flow (no `policy`): points at the Modify section + support rather than
    //    promising self-serve edits. Mirrors the plugin.
    //  - trip pages (`policy` passed): the booking exists and its pickup is already
    //    flight-driven, so reassure that delays are handled without the passenger acting.
    modificationBooking: {
        value: '$0',
        title: 'Free changes',
        subtitle: 'Yes, you can modify your booking at no extra cost. Just go to the "Modify" section after booking and connect with our support team to make changes.',
    },
    modificationTrip: {
        value: '$0',
        title: 'No modification required',
        subtitle: "If your flight is delayed or cancelled, the pickup time will automatically adjust accordingly — you don't need to worry!",
    },
    refund: {value: '100%', title: 'Full refund', qualifier: '(No Charge on your Card)', subtitle: 'You get your full fare back whenever you cancel'},
};

// Where a passenger is sent when the app can't offer the change itself. Both come from
// /config/app (general.support_phone / general.support_email) — they were hardcoded here
// until 2026-08-26. Read through the getters INSIDE the component body, never at module
// scope: the getters return undefined until /config/app lands, and a module-level read
// captures that emptiness for the life of the page.

// cents → "$0" / "$25" / "$25.50"
const formatFeeCents = (cents) => {
    const dollars = (cents ?? 0) / 100;
    return `$${dollars % 1 === 0 ? dollars : dollars.toFixed(2)}`;
};

// One tier row: big value on the left, title + API `label` subtitle on the right.
function TierRow({value, title, qualifier, subtitle}) {
    return (
        <div className="hopn-cmr-tier">
            <span className="hopn-cmr-tier-value">{value}</span>
            <div className="hopn-cmr-tier-text">
                <h5>{title}{qualifier && <span className="hopn-cmr-tier-qual"> {qualifier}</span>}</h5>
                {/* Font/colour come from .hopn-cmr-tier-text; <p> only drops the global margin. */}
                {subtitle && <p className="mb-0!">{subtitle}</p>}
            </div>
        </div>
    );
}

// Grey rounded callout closing out a tier list. `icon`/`title` render the head;
// the Refund note has neither and goes straight to its body.
function Note({icon, title, children}) {
    return (
        <div className="hopn-cmr-note">
            {title && (
                <div className="hopn-cmr-note-head">
                    {icon && <img src={icon} alt=""/>}
                    <h6>{title}</h6>
                </div>
            )}
            {children}
        </div>
    );
}

// Where the tiers come from:
//  - Booking flow: omit `policy` → reads the /calculate-policies cache via
//    POLICY_CONSTANTS (warm behind HopnModal's gate).
//  - Trip pages (RideEdit/RideConfirm/RideDetails): that cache is cold, so pass the
//    trip's `policy_popup` — which carries the same `cancellation.tiers` /
//    `modification.tiers` arrays.
//
// `tabActions` = {cancellation?, modification?, refund?} of nodes, each rendered
// right-aligned under the tab bar while ITS tab is active. Used by the RideDetails
// policy card, where "Cancel" belongs to the Cancellation tab and "Edit" to the
// Modification tab — the same pairing the pre-tab layout had next to each heading.
// `underway` — the trip has reached EnRoute or beyond, so none of the three policies
// applies any more and every tab shows one line saying so (see UNDERWAY_NOTICE).
//
// ⚠️ PASSED IN, NOT DERIVED. This component is shared with the BOOKING flow, where no trip
// exists at all and the tiers are the whole point of the panel — it must not go looking for
// a status it will not find. `RideDetails` passes `isTripUnderway(trip)`.
export function CancellationModificationRefundContent({initialTab = 'cancellation', policy, tabActions, underway = false}) {
    const [tab, setTab] = useState(initialTab);
    // Support contacts from /config/app — read here, in the body, so they pick up the
    // config as soon as it lands (see the note by the imports).
    // `|| ''` is a structural guard, not a default: the tel: href calls .replace() on this
    // and the getter is undefined until /config/app resolves. An empty string renders an
    // empty link rather than throwing; the real value arrives on the next render.
    const SUPPORT_PHONE = BOOKING_CONSTRAINTS.CONTACT_NUMBER || '';
    const SUPPORT_EMAIL = BOOKING_CONSTRAINTS.SUPPORT_EMAIL || '';
    // RAW tiers — one row per tier. From policy_popup when a trip is supplied, else
    // from POLICY_CONSTANTS (the booking-flow cache).
    const cancellationTiers = policy ? (policy.cancellation?.tiers ?? []) : POLICY_CONSTANTS.CANCELLATION_TIERS;
    const modificationTiers = policy ? (policy.modification?.tiers ?? []) : POLICY_CONSTANTS.MODIFICATION_TIERS;
    // A modification tier that can only change the pickup time (scheduled_at only).
    const isPickupOnly = (t) => {
        const f = t.editable_fields || [];
        return f.length === 1 && f[0] === 'scheduled_at';
    };

    return (
        /* `hopn-cmr--underway` drops the panel's min-height: that floor exists so switching
           between three tabs of different lengths doesn't resize the popup, and once every
           tab is a single line there is nothing to keep level — only 350px of white space
           under one sentence. */
        <div className={`hopn-cmr${underway ? ' hopn-cmr--underway' : ''}`}>
            {/* Tab bar — one grey track with a black pill that slides to the active tab. */}
            <div className="hopn-cmr-tabs" role="tablist">
                <span
                    className="hopn-cmr-tab-indicator"
                    style={{transform: `translateX(${TABS.findIndex((t) => t.key === tab) * 100}%)`}}
                    aria-hidden="true"
                />
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={`hopn-cmr-tab ${tab === t.key ? 'active' : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Per-tab action (Cancel / Edit on the trip page). Sits between the tab bar
                and the tiers so it reads as "the action for this tab", the way it used to
                sit beside the section heading. */}
            {tabActions?.[tab] && <div className="hopn-cmr-action">{tabActions[tab]}</div>}

            <div className="hopn-cmr-panel">
                {/* ⚠️ SHORT-CIRCUITS THE WHOLE PANEL. Every branch below reads tiers the
                    backend still reports for a trip already under way — they are not wrong,
                    they are just no longer available to the passenger, and rendering them
                    beside this notice would be the card contradicting itself. */}
                {underway ? (
                    <BlockedNotice>{UNDERWAY_NOTICE[tab]}</BlockedNotice>
                ) : (<>
                {tab === 'cancellation' && (cancellationTiers.length ? (
                    <>
                        {cancellationTiers.map((t) => (
                            <TierRow
                                key={t.band}
                                value={formatFeeCents(t.flat_fee_cents)}
                                title={t.is_free ? 'Free cancellation' : 'Late cancellation fee'}
                                subtitle={t.label}
                            />
                        ))}
                        {/* Shown for every trip direction — the fee it explains is not
                            airport-specific. */}
                        <Note icon={supportingPerson} title="Supporting your driver">
                            <p className="mb-0!">The fee goes directly to your driver who has already
                                committed to your trip and is on the way.</p>
                        </Note>
                    </>
                ) : (
                    <TierRow {...FREE_FALLBACK.cancellation}/>
                ))}

                {tab === 'modification' && (
                    <>
                        {modificationTiers.length ? (
                            <>
                                {modificationTiers.map((t) => {
                                    // Band 0 (closest to pickup) usually allows no edits — show a
                                    // plain "No changes" instead of the contradictory "One change
                                    // (no changes)". Otherwise one change, scoped by editable_fields.
                                    const noChanges = !t.can_edit || !(t.editable_fields || []).length;
                                    return (
                                        <TierRow
                                            key={t.band}
                                            value="$0"
                                            title={noChanges ? 'No changes' : 'One change'}
                                            qualifier={noChanges ? undefined : isPickupOnly(t) ? '(only pickup time edit)' : '(whole trip edit)'}
                                            subtitle={t.label}
                                        />
                                    );
                                })}
                                {/* Only meaningful when the trip CAN be modified — how a delayed
                                    flight is handled (to- AND from-airport), whether or not the
                                    backend sent bands. */}
                                <Note icon={infoIconGrey} title="Flight delayed?">
                                    <p className="mb-0!">We'll recommend a new pickup time based on your flight,
                                        subject to your approval.<br/>
                                        Note: Automatic updates won't preserve any custom pickup time you've
                                        selected.</p>
                                </Note>
                            </>
                        ) : (
                            <>
                                {policy ? (
                                    /* Trip pages (edit / confirm / details): a real booking exists,
                                       so no "default" row — point at the humans who can still make
                                       a change. */
                                    <Note icon={infoIconGrey} title="Still need to change something?">
                                        <p className="mb-0!">If you'd still like to modify anything, contact our admin
                                            at <a href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`}>{SUPPORT_PHONE}</a> or <a
                                                href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
                                    </Note>
                                ) : (
                                    /* Booking flow: nothing is booked yet — mirror the plugin's
                                       "Free changes, you can modify until confirmed" row. */
                                    <TierRow {...FREE_FALLBACK.modificationBooking}/>
                                )}
                            </>
                        )}
                    </>
                )}

                {tab === 'refund' && (
                    <>
                        {cancellationTiers.length ? cancellationTiers.map((t) => {
                            // A tier expresses its refund as EITHER a flat fee deducted
                            // ($X) or a percentage of the fare returned (Y%). Show the
                            // flat fee when the API sets one (> 0), else the percentage.
                            const cents = t.flat_fee_cents;
                            const hasFlatFee = typeof cents === 'number' && cents > 0;
                            const pct = t.refund_percentage ?? 0;
                            const value = hasFlatFee ? formatFeeCents(cents) : `${pct}%`;
                            // A flat fee OR a partial (0 < pct < 100) refund both mean the
                            // card was charged; only a 100% refund leaves the card untouched.
                            const isFullRefund = !hasFlatFee && pct >= 100;
                            const isNoRefund = !hasFlatFee && pct <= 0;
                            const title = isFullRefund ? 'Full refund' : isNoRefund ? 'No refund' : 'Charge';
                            const qualifier = isFullRefund ? '(No Charge on your Card)' : undefined;
                            return (
                                <TierRow
                                    key={t.band}
                                    value={value}
                                    title={title}
                                    qualifier={qualifier}
                                    subtitle={t.label}
                                />
                            );
                        }) : (
                            <TierRow {...FREE_FALLBACK.refund}/>
                        )}
                        {/* When a tier deducts a flat fee (shown as $X above), explain where
                            it goes — the SAME note as the Cancellation tab, since that fee is
                            what reduces the refund. Only when some tier actually charges one. */}
                        {cancellationTiers.some((t) => typeof t.flat_fee_cents === 'number' && t.flat_fee_cents > 0) && (
                            <Note icon={supportingPerson} title="This fee helps cover your driver's reserved time"/>
                        )}
                        {/* Also outside the tiers branch — bank timing and the Hop'n-cancelled
                            case apply regardless of which bands came back. */}
                        <Note>
                            <p className="mb-0!">Refunds typically appear within 1–3 business days,
                                depending on your bank</p>
                            <ul>
                                <li>Full refund if your ride is canceled by Hop'n due to unforeseen
                                    circumstances</li>
                            </ul>
                            <p className="mb-0!">By using Hop'n, you agree to this refund policy.</p>
                        </Note>
                    </>
                )}
                </>)}
            </div>

        </div>
    );
}

export default CancellationModificationRefundContent;
